import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/api';
import './TaskTemplate.css';
import { fetchAuthSession } from 'aws-amplify/auth';
import { toast } from 'react-hot-toast';

const TaskTemplate = () => {
  const [templates, setTemplates] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [creators, setCreators] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [templateStatus, setTemplateStatus] = useState({ type: '', message: '' });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    frequency: 'daily',
    priority: 'medium',
    job_type: '',
    assignees: [],
    is_active: true
  });
  
  const { getAccessToken } = useAuth();
  
  // Add new state for bulk copy modal
  const [isBulkCopyModalOpen, setBulkCopyModalOpen] = useState(false);
  const [selectedClientTemplates, setSelectedClientTemplates] = useState(null);
  
  // Replace separate employee/client search with unified search
  const [newAssigneeSearchQuery, setNewAssigneeSearchQuery] = useState('');
  const [newAssigneeSearchResults, setNewAssigneeSearchResults] = useState([]);
  
  // Add state for selected assignees in bulk copy modal
  const [selectedNewAssignees, setSelectedNewAssignees] = useState([]);
  
  // Add state for task creation modal
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [taskFormData, setTaskFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    assignees: [],
    job_type: '',
    status: 'active'
  });
  
  // Add state for task assignee search
  const [taskAssigneeSearchQuery, setTaskAssigneeSearchQuery] = useState('');
  const [taskAssigneeSearchResults, setTaskAssigneeSearchResults] = useState([]);
  
  // Add separate state for task edit modal
  const [isTaskEditModalOpen, setIsTaskEditModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editingTaskIndex, setEditingTaskIndex] = useState(null);
  const [taskEditFormData, setTaskEditFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    assignees: [],
    job_type: '',
    status: 'active'
  });
  
  // Add state for task edit assignee search
  const [taskEditAssigneeSearchQuery, setTaskEditAssigneeSearchQuery] = useState('');
  const [taskEditAssigneeSearchResults, setTaskEditAssigneeSearchResults] = useState([]);
  
  useEffect(() => {
    fetchTemplates();
    fetchCreators();
    checkAdminStatus();
  }, []);

  const fetchCreators = async () => {
    try {
      const token = await getAccessToken();
      const response = await axios.get(API_ENDPOINTS.EMPLOYEES.LIST, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const creatorMap = {};
      response.data.forEach(employee => {
        creatorMap[employee.user_id] = `${employee.first_name} ${employee.last_name}`;
      });
      setCreators(creatorMap);
    } catch (error) {
      console.error('Error fetching creators:', error);
    }
  };
  
  const fetchTemplates = async () => {
    try {
      console.log('DEBUG: fetchTemplates called');
      const token = await getAccessToken();
      const response = await axios.get(API_ENDPOINTS.TASK_TEMPLATES, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('DEBUG: Fetched templates response:', response.data);
      
      // Log details about the specific template we're working with
      const onboardingTemplate = response.data.find(t => t._id === '686f184c00b97d65697f80ef');
      if (onboardingTemplate) {
        console.log('DEBUG: Onboarding template found, tasks count:', onboardingTemplate.tasks?.length);
        console.log('DEBUG: Onboarding template tasks:', onboardingTemplate.tasks);
        
        // Check if task 35 (Hyperwallet) is still there
        const hyperwalletTask = onboardingTemplate.tasks?.find((task, index) => 
          (task.title === 'Hyperwallet' || index === 35)
        );
        if (hyperwalletTask) {
          console.log('DEBUG: Hyperwallet task still exists!', hyperwalletTask);
        } else {
          console.log('DEBUG: Hyperwallet task successfully removed');
        }
      } else {
        console.log('DEBUG: Onboarding template not found');
      }
      
      setTemplates(response.data);
      
      // Initialize any new groups as collapsed, but preserve existing state
      const newGroups = {};
      response.data.forEach(template => {
        const clientAssignee = template.assignees?.find(a => a.type === 'client');
        const clientName = clientAssignee ? clientAssignee.name : 'unassigned';
        const groupKey = template.frequency === 'once' 
          ? `${clientName} - ${template.title}` 
          : clientName;
        if (expandedGroups[groupKey] === undefined) {
          newGroups[groupKey] = false; // New groups start collapsed
        }
      });
      
      setExpandedGroups(prev => ({
        ...prev,
        ...newGroups
      }));
      console.log('DEBUG: Templates state updated');
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Group templates by client and template type
  const groupedTemplates = templates.reduce((groups, template) => {
    const clientAssignee = template.assignees?.find(a => a.type === 'client');
    const clientName = clientAssignee ? clientAssignee.name : 'unassigned';
    
    // Create groups with template title for all templates
    const groupKey = clientName === 'unassigned' 
      ? `Unassigned - ${template.title}`
      : `${clientName} - ${template.title}`;
    
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(template);
    return groups;
  }, {});

  const searchAssignees = async (query) => {
    if (!query) {
      setSearchResults([]);
      return;
    }
    try {
      const token = await getAccessToken();
      const response = await axios.get(`${API_ENDPOINTS.TASKS}/search_assignees?query=${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.assignees || []);
    } catch (error) {
      console.error('Error searching assignees:', error);
    }
  };
  
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    searchAssignees(query);
  };
  
  const handleSelectAssignee = (assignee) => {
    const newAssignees = [...selectedAssignees, assignee];
    setSelectedAssignees(newAssignees);
    setFormData({ ...formData, assignees: newAssignees });
    setSearchQuery('');
    setSearchResults([]);
  };
  
  const handleRemoveAssignee = (assigneeId) => {
    const newAssignees = selectedAssignees.filter(a => a.id !== assigneeId);
    setSelectedAssignees(newAssignees);
    setFormData({ ...formData, assignees: newAssignees });
  };
  
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      frequency: 'daily',
      priority: 'medium',
      job_type: '',
      assignees: [],
      is_active: true
    });
    setSelectedAssignees([]);
    setEditingTemplate(null);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      title: template.title,
      description: template.description,
      frequency: template.frequency,
      priority: template.priority,
      job_type: template.job_type,
      assignees: template.assignees,
      is_active: template.is_active
    });
    setSelectedAssignees(template.assignees || []);
    setIsOpen(true);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAdmin) {
      setTemplateStatus({ type: 'error', message: 'You do not have permission to create or edit templates' });
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 4000);
      return;
    }
    
    try {
      const token = await getAccessToken();
      if (editingTemplate) {
        const updateData = {
          ...editingTemplate,
          title: formData.title,
          description: formData.description,
          frequency: formData.frequency,
          priority: formData.priority,
          job_type: formData.job_type,
          assignees: formData.assignees,
          is_active: formData.is_active
        };
        await axios.put(`${API_ENDPOINTS.TASK_TEMPLATES}/${editingTemplate._id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setTemplateStatus({ type: 'success', message: 'Template updated successfully!' });
      } else {
        await axios.post(API_ENDPOINTS.TASK_TEMPLATES, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setTemplateStatus({ type: 'success', message: 'Template created successfully!' });
      }
      
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 3000);
      setIsOpen(false);
      fetchTemplates();
      resetForm();
    } catch (error) {
      console.error('Error saving template:', error);
      if (error.response?.status === 403) {
        setTemplateStatus({ type: 'error', message: 'You do not have permission to perform this action' });
      } else {
        setTemplateStatus({ type: 'error', message: 'Failed to save template. Please try again.' });
      }
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 4000);
    }
  };
  
  const handleToggleActive = async (templateId, currentStatus) => {
    try {
      const token = await getAccessToken();
      const template = templates.find(t => t._id === templateId);
      await axios.put(`${API_ENDPOINTS.TASK_TEMPLATES}/${templateId}`, {
        ...template,
        is_active: !currentStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTemplateStatus({ 
        type: 'success', 
        message: `Template ${!currentStatus ? 'activated' : 'deactivated'} successfully!` 
      });
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 3000);
      fetchTemplates();
    } catch (error) {
      console.error('Error updating template:', error);
      setTemplateStatus({ type: 'error', message: 'Failed to update template status' });
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 4000);
    }
  };
  
  const handleDuplicate = async (template) => {
    try {
      const token = await getAccessToken();
      await axios.post(`${API_ENDPOINTS.TASK_TEMPLATES}/${template._id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTemplateStatus({ type: 'success', message: 'Template duplicated successfully!' });
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 3000);
      fetchTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      setTemplateStatus({ type: 'error', message: 'Failed to duplicate template' });
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 4000);
    }
  };
  
  // Update handleBulkCopy with debugging and fixes
  const handleBulkCopy = async () => {
    try {
      if (selectedNewAssignees.length === 0) {
        setTemplateStatus({ type: 'error', message: 'Please select at least one employee or client to assign to the copied templates' });
        setTimeout(() => setTemplateStatus({ type: '', message: '' }), 4000);
        return;
      }

      const token = await getAccessToken();
      
      // Format new assignees for consistent use
      const formattedNewAssignees = selectedNewAssignees.map(assignee => ({
        id: assignee.id,
        name: assignee.name,
        type: assignee.type,
        client_id: assignee.type === 'client' ? assignee.id : null,
        employee_id: assignee.type === 'employee' ? assignee.id : null
      }));
      
      // Create copies of all templates with ONLY the new assignees
      const copyPromises = selectedClientTemplates.map(template => {
        // Create new template object without _id and with only new assignees
        const { _id, assignees, ...templateWithoutIdAndAssignees } = template;
        
        // Update tasks to have the new assignees as well
        const updatedTasks = template.tasks ? template.tasks.map(task => ({
          ...task,
          assignees: formattedNewAssignees // Replace task assignees with new ones
        })) : [];
        
        const newTemplate = {
          ...templateWithoutIdAndAssignees,
          assignees: formattedNewAssignees,
          tasks: updatedTasks
        };
        
        return axios.post(API_ENDPOINTS.TASK_TEMPLATES, newTemplate, {
          headers: { Authorization: `Bearer ${token}` }
        });
      });
      
      await Promise.all(copyPromises);
      
      setTemplateStatus({ 
        type: 'success', 
        message: `Successfully copied ${selectedClientTemplates.length} template(s) with new assignees!` 
      });
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 3000);
      
      setBulkCopyModalOpen(false);
      setSelectedClientTemplates(null);
      setNewAssigneeSearchQuery('');
      setNewAssigneeSearchResults([]);
      setSelectedNewAssignees([]);
      fetchTemplates();
    } catch (error) {
      console.error('Error bulk copying templates:', error);
      console.error('Error details:', error.response?.data);
      setTemplateStatus({ type: 'error', message: 'Failed to copy templates' });
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 4000);
    }
  };
  
  // Add unified search function for bulk copy modal
  const searchAllAssignees = async (query) => {
    if (!query) {
      setNewAssigneeSearchResults([]);
      return;
    }
    try {
      const token = await getAccessToken();
      const response = await axios.get(`${API_ENDPOINTS.TASKS}/search_assignees?query=${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Return all results (both employees and clients)
      setNewAssigneeSearchResults(response.data.assignees || []);
    } catch (error) {
      console.error('Error searching assignees:', error);
      setNewAssigneeSearchResults([]);
    }
  };

  // Add search function for task creation assignees
  const searchTaskAssignees = async (query) => {
    if (!query) {
      setTaskAssigneeSearchResults([]);
      return;
    }
    try {
      const token = await getAccessToken();
      const response = await axios.get(`${API_ENDPOINTS.TASKS}/search_assignees?query=${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTaskAssigneeSearchResults(response.data.assignees || []);
    } catch (error) {
      console.error('Error searching task assignees:', error);
      setTaskAssigneeSearchResults([]);
    }
  };

  // Add search function for task edit assignees
  const searchTaskEditAssignees = async (query) => {
    if (!query) {
      setTaskEditAssigneeSearchResults([]);
      return;
    }
    try {
      const token = await getAccessToken();
      const response = await axios.get(`${API_ENDPOINTS.TASKS}/search_assignees?query=${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTaskEditAssigneeSearchResults(response.data.assignees || []);
    } catch (error) {
      console.error('Error searching task edit assignees:', error);
      setTaskEditAssigneeSearchResults([]);
    }
  };
  
  // Update handleDelete function with correct endpoint
  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }
    
    try {
      const token = await getAccessToken();
      await axios.delete(`${API_ENDPOINTS.TASK_TEMPLATES}/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTemplateStatus({ type: 'success', message: 'Template deleted successfully!' });
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 3000);
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      let errorMessage = 'Failed to delete template';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      setTemplateStatus({ type: 'error', message: errorMessage });
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 5000);
    }
  };
  
  // Update handleDeleteGroup function with correct endpoint
  const handleDeleteGroup = async (templates) => {
    // Check if any templates have tasks
    const templatesWithTasks = templates.filter(t => (t.tasks?.length || 0) > 0);
    if (templatesWithTasks.length > 0) {
      setTemplateStatus({ 
        type: 'error', 
        message: `Cannot delete ${templatesWithTasks.length} template(s) that still have tasks. Please delete all tasks first.` 
      });
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 5000);
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete all templates for this client?')) {
      return;
    }
    
    try {
      const token = await getAccessToken();
      const deletePromises = templates.map(template => 
        axios.delete(`${API_ENDPOINTS.TASK_TEMPLATES}/${template._id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );
      await Promise.all(deletePromises);
      
      setTemplateStatus({ 
        type: 'success', 
        message: `Successfully deleted ${templates.length} template(s)!` 
      });
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 3000);
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting templates:', error);
      let errorMessage = 'Failed to delete templates';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      setTemplateStatus({ type: 'error', message: errorMessage });
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 5000);
    }
  };
  
  // Add admin check
  const checkAdminStatus = async () => {
    try {
      const { tokens } = await fetchAuthSession();
      const groups = tokens.accessToken.payload['cognito:groups'] || [];
      setIsAdmin(groups.includes('ADMIN'));
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    }
  };

  // Function to open task creation modal
  const openTaskModal = (template) => {
    setSelectedTemplate(template);
    
    // Don't pre-fill form - start with empty fields
    setTaskFormData({
      title: '',
      description: '',
      priority: 'medium',
      due_date: '',
      assignees: template.assignees || [], // Keep assignees from template
      job_type: template.job_type, // Keep job type from template
      status: 'active'
    });
    
    setIsTaskModalOpen(true);
  };

  // Function to open task edit modal
  const openTaskEditModal = (template, task, taskIndex) => {
    console.log('Full task object:', task);
    console.log('Task keys:', Object.keys(task));
    console.log('Task.id:', task.id);
    console.log('Task._id:', task._id);
    console.log('Task index:', taskIndex);
    
    setSelectedTemplate(template);
    setEditingTask(task);
    // Use task.id if it exists, otherwise use the taskIndex for legacy tasks
    setEditingTaskIndex(task.id || taskIndex);
    
    // Pre-fill form with existing task data
    setTaskEditFormData({
      title: task.title,
      description: task.description,
      priority: task.priority,
      due_date: task.due_date || '',
      assignees: task.assignees || template.assignees || [],
      job_type: task.job_type,
      status: task.status || 'active'
    });
    
    setIsTaskEditModalOpen(true);
  };

  // Function to close task modal
  const closeTaskModal = () => {
    setIsTaskModalOpen(false);
    setSelectedTemplate(null);
    setTaskFormData({
      title: '',
      description: '',
      priority: 'medium',
      due_date: '',
      assignees: [],
      job_type: '',
      status: 'active',
      task_id: null // Explicitly clear the task_id
    });
    setTaskAssigneeSearchQuery('');
    setTaskAssigneeSearchResults([]);
  };

  // Function to close task edit modal
  const closeTaskEditModal = () => {
    setIsTaskEditModalOpen(false);
    setSelectedTemplate(null);
    setEditingTask(null);
    setEditingTaskIndex(null);
    setTaskEditFormData({
      title: '',
      description: '',
      priority: 'medium',
      due_date: '',
      assignees: [],
      job_type: '',
      status: 'active'
    });
    setTaskEditAssigneeSearchQuery('');
    setTaskEditAssigneeSearchResults([]);
  };

  // Function to add assignee to task
  const handleAddTaskAssignee = (assignee) => {
    const currentAssignees = taskFormData.assignees || [];
    if (!currentAssignees.find(a => a.id === assignee.id)) {
      setTaskFormData({ 
        ...taskFormData, 
        assignees: [...currentAssignees, assignee] 
      });
    }
    setTaskAssigneeSearchQuery('');
    setTaskAssigneeSearchResults([]);
  };

  // Function to remove assignee from task
  const handleRemoveTaskAssignee = (assigneeId) => {
    setTaskFormData({ 
      ...taskFormData, 
      assignees: taskFormData.assignees.filter(a => a.id !== assigneeId) 
    });
  };

  // Function to add assignee to task edit
  const handleAddTaskEditAssignee = (assignee) => {
    const currentAssignees = taskEditFormData.assignees || [];
    if (!currentAssignees.find(a => a.id === assignee.id)) {
      setTaskEditFormData({ 
        ...taskEditFormData, 
        assignees: [...currentAssignees, assignee] 
      });
    }
    setTaskEditAssigneeSearchQuery('');
    setTaskEditAssigneeSearchResults([]);
  };

  // Function to remove assignee from task edit
  const handleRemoveTaskEditAssignee = (assigneeId) => {
    setTaskEditFormData({ 
      ...taskEditFormData, 
      assignees: taskEditFormData.assignees.filter(a => a.id !== assigneeId) 
    });
  };

  // Function to handle task editing
  const handleEditTask = async (e) => {
    e.preventDefault();
    try {
      const token = await getAccessToken();
      
      console.log('Editing task:', editingTask);
      console.log('Task ID/Index:', editingTaskIndex);
      console.log('Template ID:', selectedTemplate._id);
      
      // Process assignees to match backend expected format
      const processedAssignees = taskEditFormData.assignees.map(assignee => ({
        id: assignee.id,
        name: assignee.name,
        type: assignee.type,
        client_id: assignee.type === 'client' ? assignee.id : null,
        employee_id: assignee.type === 'employee' ? assignee.id : null
      }));

      const taskData = {
        title: taskEditFormData.title,
        description: taskEditFormData.description,
        priority: taskEditFormData.priority,
        due_date: taskEditFormData.due_date,
        job_type: taskEditFormData.job_type,
        assignees: processedAssignees
      };

      console.log('Sending PUT request to:', `${API_ENDPOINTS.TASK_TEMPLATES}/${selectedTemplate._id}/tasks/${editingTaskIndex}`);
      console.log('Task data:', taskData);

      const response = await axios.put(
        `${API_ENDPOINTS.TASK_TEMPLATES}/${selectedTemplate._id}/tasks/${editingTaskIndex}`,
        taskData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setTemplateStatus({ type: 'success', message: `Task "${taskEditFormData.title}" updated successfully!` });
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 3000);
      closeTaskEditModal();
      fetchTemplates(); // Refresh templates to show the changes
    } catch (error) {
      console.error('Error updating task:', error);
      console.error('Error response:', error.response?.data);
      setTemplateStatus({ type: 'error', message: 'Failed to update task' });
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 4000);
    }
  };

  // Function to handle task creation/editing
  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const token = await getAccessToken();
      
      // Process assignees to match backend expected format
      const processedAssignees = taskFormData.assignees.map(assignee => ({
        id: assignee.id,
        name: assignee.name,
        type: assignee.type,
        client_id: assignee.type === 'client' ? assignee.id : null,
        employee_id: assignee.type === 'employee' ? assignee.id : null
      }));

      const taskData = {
        title: taskFormData.title,
        description: taskFormData.description,
        priority: taskFormData.priority,
        due_date: taskFormData.due_date,
        job_type: taskFormData.job_type,
        assignees: processedAssignees
      };

      // Create new task
      const response = await axios.post(
        `${API_ENDPOINTS.TASK_TEMPLATES}/${selectedTemplate._id}/tasks`,
        taskData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setTemplateStatus({ type: 'success', message: `Task "${taskFormData.title}" added to template successfully!` });
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 3000);
      closeTaskModal();
      fetchTemplates(); // Refresh templates to show the changes
    } catch (error) {
      console.error('Error adding task:', error);
      setTemplateStatus({ type: 'error', message: 'Failed to add task to template' });
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 4000);
    }
  };

  // Function to handle task deletion from a specific template
  const handleDeleteTaskFromTemplate = async (templateId, task, taskIndex) => {
    console.log('DEBUG: handleDeleteTaskFromTemplate called');
    console.log('DEBUG: templateId:', templateId);
    console.log('DEBUG: task:', task);
    console.log('DEBUG: taskIndex:', taskIndex);
    
    if (!window.confirm('Are you sure you want to delete this task from the template?')) {
      console.log('DEBUG: User cancelled deletion');
      return;
    }

    try {
      const token = await getAccessToken();
      
      // Use task.id if it exists, otherwise use taskIndex for legacy tasks
      const taskIdentifier = task.id || taskIndex;
      console.log('DEBUG: Using taskIdentifier:', taskIdentifier);
      
      const deleteUrl = `${API_ENDPOINTS.TASK_TEMPLATES}/${templateId}/tasks/${taskIdentifier}`;
      console.log('DEBUG: Delete URL:', deleteUrl);
      
      console.log('DEBUG: Making DELETE request...');
      const response = await axios.delete(deleteUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('DEBUG: Delete response:', response.data);
      console.log('DEBUG: Delete status:', response.status);
      
      console.log('DEBUG: Calling fetchTemplates to refresh data...');
      await fetchTemplates(); // Make sure this completes before continuing
      console.log('DEBUG: fetchTemplates completed');
      
      setTemplateStatus({ type: 'success', message: 'Task deleted successfully!' });
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 3000);
    } catch (error) {
      console.error('ERROR: Error deleting task from template:', error);
      if (error.response) {
        console.error('ERROR: Error details:', error.response.data);
        console.error('ERROR: Error status:', error.response.status);
      }
      setTemplateStatus({ type: 'error', message: 'Failed to delete task from template' });
      setTimeout(() => setTemplateStatus({ type: '', message: '' }), 4000);
    }
  };

  const handleSendTasks = async (templateId) => {
    if (window.confirm('Are you sure you want to create tasks from this template?')) {
      try {
        const token = await getAccessToken();
        const response = await axios.post(`${API_ENDPOINTS.TASKS}/create-from-template/${templateId}`, {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.status === 'success') {
          setTemplateStatus({ 
            type: 'success', 
            message: `Successfully created ${response.data.created_count} tasks from template!` 
          });
          setTimeout(() => setTemplateStatus({ type: '', message: '' }), 3000);
        } else {
          setTemplateStatus({ type: 'error', message: 'Failed to create tasks from template' });
          setTimeout(() => setTemplateStatus({ type: '', message: '' }), 4000);
        }
      } catch (error) {
        console.error('Error creating tasks from template:', error);
        setTemplateStatus({ type: 'error', message: 'Failed to create tasks from template' });
        setTimeout(() => setTemplateStatus({ type: '', message: '' }), 4000);
      }
    }
  };
  
  return (
    <div className="task-template">
      <div className="task-template__header">
        <h1>Task Templates</h1>
        <button 
          className="task-template__create-button"
          onClick={() => {
            resetForm();
            setIsOpen(true);
          }}
        >
          Create Template
        </button>
      </div>

      {/* Template Status Message */}
      {templateStatus.message && (
        <div className={`template-status-message template-status-${templateStatus.type}`}>
          {templateStatus.message}
        </div>
      )}
      
      <div className="task-template__groups">
        {Object.entries(groupedTemplates).map(([clientName, clientTemplates]) => (
          <div key={clientName} className="template-group">
            <div 
              className="template-group__header"
              onClick={() => toggleGroup(clientName)}
            >
              <div className="template-group__header-content">
                <h2>{clientName}</h2>
                <span className="template-group__count">
                  {clientTemplates.reduce((total, template) => total + (template.tasks?.length || 0), 0)} tasks
                </span>
              </div>
              <div className="template-group__actions">
                <button
                  className="task-template__create-task-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Use the first template in the group - for onboarding templates there's only one
                    openTaskModal(clientTemplates[0]);
                  }}
                  title="Add task to template"
                >
                  <i className="fas fa-plus"></i>
                </button>
                <button
                  className="task-template__copy-all-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('Templates to copy for client:', clientName, clientTemplates);
                    setSelectedClientTemplates([...clientTemplates]);
                    setBulkCopyModalOpen(true);
                  }}
                >
                  <i className="far fa-copy"></i>
                </button>
                <button
                  className="task-template__delete-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteGroup(clientTemplates);
                  }}
                  disabled={clientTemplates.some(t => (t.tasks?.length || 0) > 0)}
                  title={clientTemplates.some(t => (t.tasks?.length || 0) > 0) ? "Delete all tasks before deleting templates" : "Delete all templates"}
                  style={{
                    opacity: clientTemplates.some(t => (t.tasks?.length || 0) > 0) ? 0.5 : 1,
                    cursor: clientTemplates.some(t => (t.tasks?.length || 0) > 0) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <i className="fas fa-trash-alt"></i>
                </button>
              </div>
              <button className="template-group__toggle">
                {expandedGroups[clientName] ? '−' : '+'}
              </button>
            </div>
            
            {expandedGroups[clientName] && (
              <div className="template-group__content">
                <table className="task-template__table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Job Type</th>
                      <th>Assignees</th>
                      <th>Frequency</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientTemplates.map((template) => (
                      <tr key={template._id}>
                        <td>{template.frequency === 'once' ? `${template.title} TEMPLATE` : template.title}</td>
                        <td>{template.job_type}</td>
                        <td>
                          <div className="assignee-tags">
                            {template.assignees?.filter(a => a.type === 'client').map(assignee => (
                              <span key={assignee.id} className="assignee-tag">
                                {assignee.name}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${
                            template.frequency === 'daily' ? 'badge--green' : 
                            template.frequency === 'weekly' ? 'badge--blue' : 
                            template.frequency === 'once' ? 'badge--gray' : 'badge--blue'
                          }`}>
                            {template.frequency}
                          </span>
                        </td>
                        <td>
                          <span className={`badge badge--${template.priority}`}>
                            {template.priority}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${template.is_active ? 'badge--green' : 'badge--gray'}`}>
                            {template.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="task-template__actions">
                          <button
                            className="task-template__edit-button"
                            onClick={() => handleEdit(template)}
                          >
                            <i className="fas fa-pencil-alt"></i>
                          </button>
                          <button
                            className="task-template__delete-button"
                            onClick={() => handleDelete(template._id)}
                            disabled={(template.tasks?.length || 0) > 0}
                            title={(template.tasks?.length || 0) > 0 ? "Delete all tasks before deleting template" : "Delete template"}
                            style={{
                              opacity: (template.tasks?.length || 0) > 0 ? 0.5 : 1,
                              cursor: (template.tasks?.length || 0) > 0 ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={template.is_active}
                              onChange={() => handleToggleActive(template._id, template.is_active)}
                            />
                            <span className="slider"></span>
                          </label>
                          {template.frequency === 'once' && (
                            <button
                              className="task-template__send-tasks-button"
                              onClick={() => handleSendTasks(template._id)}
                              title="Send tasks from this template"
                            >
                              <i className="fas fa-paper-plane"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {/* Show tasks within each template */}
                    {clientTemplates.map((template) => 
                      template.tasks && template.tasks.length > 0 ? (
                        template.tasks.map((task, taskIndex) => (
                          <tr key={`${template._id}-${taskIndex}`} className="template-task-row">
                            <td className="template-task-title">
                              {task.title}
                            </td>
                            <td>{task.job_type}</td>
                            <td>
                              <div className="assignee-tags">
                                {task.assignees?.filter(a => a.type === 'client').map(assignee => (
                                  <span key={assignee.id} className="assignee-tag">
                                    {assignee.name}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td>-</td>
                            <td>
                              <span className={`badge badge--${task.priority}`}>
                                {task.priority}
                              </span>
                            </td>
                            <td>-</td>
                            <td className="task-template__actions">
                              <button
                                className="task-template__edit-button"
                                onClick={() => openTaskEditModal(template, task, taskIndex)}
                                title="Edit task"
                              >
                                <i className="fas fa-pencil-alt"></i>
                              </button>
                              <button
                                className="task-template__delete-button"
                                onClick={() => handleDeleteTaskFromTemplate(template._id, task, taskIndex)}
                                title="Remove task from template"
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : null
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
      
      {isOpen && (
        <div className="task-modal-overlay">
          <div className="task-modal">
            <div className="task-modal__header">
              <h2>{editingTemplate ? 'Edit Task Template' : 'Create Task Template'}</h2>
              <button 
                className="task-modal__close"
                onClick={() => {
                  setIsOpen(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>
            <div className="task-modal__body">
              <form onSubmit={handleSubmit}>
                <div className="task-modal__field">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="task-modal__input"
                  />
                </div>
                
                <div className="task-modal__field">
                  <label>Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    className="task-modal__input"
                  />
                </div>
                
                <div className="task-modal__field">
                  <label>Job Type *</label>
                  <input
                    type="text"
                    value={formData.job_type}
                    onChange={(e) => setFormData({ ...formData, job_type: e.target.value })}
                    placeholder="e.g. Content Team, Talent Team"
                    required
                    className="task-modal__input"
                  />
                </div>
                
                <div className="task-modal__field">
                  <label>Frequency *</label>
                  <select
                    value={formData.frequency}
                    onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                    required
                    className="task-modal__input"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="once">Once</option>
                  </select>
                </div>
                
                <div className="task-modal__field">
                  <label>Priority *</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    required
                    className="task-modal__input"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div className="task-modal__field">
                  <label>Assignees</label>
                  <div className="assignee-search">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      placeholder="Search employees or clients..."
                      className="task-modal__input"
                    />
                    {searchResults.length > 0 && (
                      <div className="assignee-search__results">
                        {searchResults.map(assignee => (
                          <div
                            key={assignee.id}
                            className="assignee-search__result"
                            onClick={() => handleSelectAssignee(assignee)}
                          >
                            <span>{assignee.name}</span>
                            <span className="assignee-type">{assignee.type}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="selected-assignees">
                    {selectedAssignees.map(assignee => (
                      <div key={assignee.id} className="selected-assignee">
                        <span>{assignee.name}</span>
                        <span className="assignee-type">{assignee.type}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAssignee(assignee.id)}
                          className="remove-assignee"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </form>
            </div>
            
            <div className="task-modal__footer">
              <button 
                type="button" 
                className="task-modal__button task-modal__button--secondary"
                onClick={() => {
                  setIsOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="task-modal__button task-modal__button--primary"
                onClick={handleSubmit}
              >
                {editingTemplate ? 'Save Changes' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add bulk copy modal */}
      {isBulkCopyModalOpen && (
        <div className="task-modal-overlay">
          <div className="task-modal">
            <div className="task-modal__header">
              <h2>Copy All Templates</h2>
              <button 
                className="task-modal__close"
                onClick={() => {
                  setBulkCopyModalOpen(false);
                  setSelectedClientTemplates(null);
                  setNewAssigneeSearchQuery('');
                  setNewAssigneeSearchResults([]);
                  setSelectedNewAssignees([]);
                }}
              >
                ×
              </button>
            </div>
            <div className="task-modal__body">
              <div className="task-modal__field">
                <label>Select New Assignees</label>
                <div className="assignee-search">
                  <input
                    type="text"
                    value={newAssigneeSearchQuery}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewAssigneeSearchQuery(value);
                      if (value.length >= 2) {
                        searchAllAssignees(value);
                      } else {
                        setNewAssigneeSearchResults([]);
                      }
                    }}
                    placeholder="Search for employees or clients..."
                    className="task-modal__input"
                  />
                  {newAssigneeSearchResults.length > 0 && (
                    <div className="assignee-search__results">
                      {newAssigneeSearchResults.map(result => (
                        <div
                          key={result.id}
                          className="assignee-search__result"
                          onClick={() => {
                            // Only add if not already selected
                            if (!selectedNewAssignees.find(a => a.id === result.id)) {
                              setSelectedNewAssignees(prev => [...prev, result]);
                            }
                            setNewAssigneeSearchQuery('');
                            setNewAssigneeSearchResults([]);
                          }}
                        >
                          <span>{result.name}</span>
                          <span className="assignee-type">{result.type}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Display selected assignees */}
                {selectedNewAssignees.length > 0 && (
                  <div className="task-modal__field">
                    <label>Selected Assignees</label>
                    <div className="selected-assignees">
                      {selectedNewAssignees.map(assignee => (
                        <div key={assignee.id} className="selected-assignee">
                          <span>{assignee.name}</span>
                          <span className="assignee-type">{assignee.type}</span>
                          <button
                            type="button"
                            className="remove-assignee"
                            onClick={() => setSelectedNewAssignees(prev => prev.filter(a => a.id !== assignee.id))}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="task-modal__footer">
              <button 
                type="button" 
                className="task-modal__button task-modal__button--secondary"
                onClick={() => {
                  setBulkCopyModalOpen(false);
                  setSelectedClientTemplates(null);
                  setNewAssigneeSearchQuery('');
                  setNewAssigneeSearchResults([]);
                  setSelectedNewAssignees([]);
                }}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="task-modal__button task-modal__button--primary"
                onClick={handleBulkCopy}
              >
                Copy Templates
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add task creation modal */}
      {isTaskModalOpen && (
        <div className="task-modal-overlay">
          <div className="task-modal">
            <div className="task-modal__header">
              <h2>Add Task to Template</h2>
              <button 
                className="task-modal__close"
                onClick={closeTaskModal}
              >
                ×
              </button>
            </div>
            <div className="task-modal__body">
              <form onSubmit={handleCreateTask}>
                <div className="task-modal__field">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={taskFormData.title}
                    onChange={(e) => setTaskFormData({ ...taskFormData, title: e.target.value })}
                    required
                    className="task-modal__input"
                  />
                </div>
                
                <div className="task-modal__field">
                  <label>Description *</label>
                  <textarea
                    value={taskFormData.description}
                    onChange={(e) => setTaskFormData({ ...taskFormData, description: e.target.value })}
                    required
                    className="task-modal__input"
                  />
                </div>
                
                <div className="task-modal__field">
                  <label>Job Type *</label>
                  <input
                    type="text"
                    value={taskFormData.job_type}
                    onChange={(e) => setTaskFormData({ ...taskFormData, job_type: e.target.value })}
                    placeholder="e.g. Content Team, Talent Team"
                    required
                    className="task-modal__input"
                  />
                </div>
                
                <div className="task-modal__field">
                  <label>Priority *</label>
                  <select
                    value={taskFormData.priority}
                    onChange={(e) => setTaskFormData({ ...taskFormData, priority: e.target.value })}
                    required
                    className="task-modal__input"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div className="task-modal__field">
                  <label>Assignees</label>
                  
                  {/* Search for additional assignees */}
                  <div className="assignee-search">
                    <input
                      type="text"
                      value={taskAssigneeSearchQuery}
                      onChange={(e) => {
                        const value = e.target.value;
                        setTaskAssigneeSearchQuery(value);
                        if (value.length >= 2) {
                          searchTaskAssignees(value);
                        } else {
                          setTaskAssigneeSearchResults([]);
                        }
                      }}
                      placeholder="Search for employees or clients to assign..."
                      className="task-modal__input"
                    />
                    {taskAssigneeSearchResults.length > 0 && (
                      <div className="assignee-search__results">
                        {taskAssigneeSearchResults.map(assignee => (
                          <div
                            key={assignee.id}
                            className="assignee-search__result"
                            onClick={() => handleAddTaskAssignee(assignee)}
                          >
                            <span>{assignee.name}</span>
                            <span className="assignee-type">{assignee.type}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Display selected assignees */}
                  <div className="selected-assignees">
                    {taskFormData.assignees.map(assignee => (
                      <div key={assignee.id} className="selected-assignee">
                        <span>{assignee.name}</span>
                        <span className="assignee-type">{assignee.type}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveTaskAssignee(assignee.id)}
                          className="remove-assignee"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </form>
            </div>
            
            <div className="task-modal__footer">
              <button 
                type="button" 
                className="task-modal__button task-modal__button--secondary"
                onClick={closeTaskModal}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="task-modal__button task-modal__button--primary"
                onClick={handleCreateTask}
              >
                Add Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add task edit modal */}
      {isTaskEditModalOpen && (
        <div className="task-modal-overlay">
          <div className="task-modal">
            <div className="task-modal__header">
              <h2>Edit Task</h2>
              <button 
                className="task-modal__close"
                onClick={closeTaskEditModal}
              >
                ×
              </button>
            </div>
            <div className="task-modal__body">
              <form onSubmit={handleEditTask}>
                <div className="task-modal__field">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={taskEditFormData.title}
                    onChange={(e) => setTaskEditFormData({ ...taskEditFormData, title: e.target.value })}
                    required
                    className="task-modal__input"
                  />
                </div>
                
                <div className="task-modal__field">
                  <label>Description *</label>
                  <textarea
                    value={taskEditFormData.description}
                    onChange={(e) => setTaskEditFormData({ ...taskEditFormData, description: e.target.value })}
                    required
                    className="task-modal__input"
                  />
                </div>
                
                <div className="task-modal__field">
                  <label>Job Type *</label>
                  <input
                    type="text"
                    value={taskEditFormData.job_type}
                    onChange={(e) => setTaskEditFormData({ ...taskEditFormData, job_type: e.target.value })}
                    placeholder="e.g. Content Team, Talent Team"
                    required
                    className="task-modal__input"
                  />
                </div>
                
                <div className="task-modal__field">
                  <label>Priority *</label>
                  <select
                    value={taskEditFormData.priority}
                    onChange={(e) => setTaskEditFormData({ ...taskEditFormData, priority: e.target.value })}
                    required
                    className="task-modal__input"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div className="task-modal__field">
                  <label>Assignees</label>
                  
                  {/* Search for additional assignees */}
                  <div className="assignee-search">
                    <input
                      type="text"
                      value={taskEditAssigneeSearchQuery}
                      onChange={(e) => {
                        const value = e.target.value;
                        setTaskEditAssigneeSearchQuery(value);
                        if (value.length >= 2) {
                          searchTaskEditAssignees(value);
                        } else {
                          setTaskEditAssigneeSearchResults([]);
                        }
                      }}
                      placeholder="Search for employees or clients to assign..."
                      className="task-modal__input"
                    />
                    {taskEditAssigneeSearchResults.length > 0 && (
                      <div className="assignee-search__results">
                        {taskEditAssigneeSearchResults.map(assignee => (
                          <div
                            key={assignee.id}
                            className="assignee-search__result"
                            onClick={() => handleAddTaskEditAssignee(assignee)}
                          >
                            <span>{assignee.name}</span>
                            <span className="assignee-type">{assignee.type}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Display selected assignees */}
                  <div className="selected-assignees">
                    {taskEditFormData.assignees.map(assignee => (
                      <div key={assignee.id} className="selected-assignee">
                        <span>{assignee.name}</span>
                        <span className="assignee-type">{assignee.type}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveTaskEditAssignee(assignee.id)}
                          className="remove-assignee"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </form>
            </div>
            
            <div className="task-modal__footer">
              <button 
                type="button" 
                className="task-modal__button task-modal__button--secondary"
                onClick={closeTaskEditModal}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="task-modal__button task-modal__button--primary"
                onClick={handleEditTask}
              >
                Update Task
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskTemplate; 